import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, ViewEncapsulation,OnDestroy, Injectable, Inject, Optional } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NativeDateAdapter, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { formatDate } from '@angular/common';
import { CreateReminderDialogService } from './create-reminder-dialog.service';
import { QnSbnDataService } from '../../services/qn-sbn-data-service.service';
import { QnSbnService } from '../../services/qn-sbn-service.service';
import { HttpErrorResponse } from '@angular/common/http';
import { OneSnackBarService, OneSnackBarType } from '@one/angular-kit/modal';
import { ReminderMessageTemplateDialogComponent } from './create-reminder/reminder-message-template-dialog/reminder-message-template-dialog.component';
import { CreateReminderComponent } from './create-reminder/create-reminder.component';
import { Subscription } from 'rxjs';

export const PICK_FORMATS = {
  parse: { dateInput: { month: 'short', year: 'numeric', day: 'numeric' } },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
}

@Injectable()
class PickDateAdapter extends NativeDateAdapter {
  public format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'input') {
      return formatDate(date, 'dd-MMM-yyyy', this.locale);
    } else {
      return date.toDateString();
    }
  }
}

type ReminderDto = {
  ReminderId?: string;
  ReminderDueDate?: string;
  ReminderSubject?: string;
  ReminderMessage?: string;
  ReminderAddTemplate?: string;
  ReminderSendAutomatically?: boolean;
  ReminderSendStatus?: string;
};

type ReminderTableItem = {
  reminderId?: string;
  reminderDate: string;
  reminderSubject: string;
  reminderMessage: string;
  reminderSendMode: string;
  reminderSendStatus: string;
  reminderAddTemplate: string;
  reminderSendAutomatically: boolean;
  reminderActionIcons: boolean;
};

@Component({
  selector: 'dl-due-date-reminder',
  templateUrl: './due-date-reminder.component.html',
  styleUrls: ['./due-date-reminder.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DateAdapter, useClass: PickDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PICK_FORMATS }
  ]
})

export class DueDateReminderComponent implements OnInit,OnDestroy {

public constructor( 
  private changeDetectorRef: ChangeDetectorRef, 
  private createReminderDialogService: CreateReminderDialogService,
  private _QnSbnDataService: QnSbnDataService, 
  private qnsbnservice : QnSbnService, 
  private readonly dialog?: MatDialog, 
  private readonly oneSnackBar?: OneSnackBarService,
  @Optional() public dialogRef?: MatDialogRef<DueDateReminderComponent>,
  @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: any
) {
  if (!dialogRef) {
    this.dueDateEnabledSub = this._QnSbnDataService.DueDateEnabled$.subscribe(res => {
      this.DueDateEnabled = res;
    });
    this.feedbackEnabledSub = this._QnSbnDataService.FeedbackEnabled$.subscribe(res => {
      this.feedbackEnabled = res;
    });
  }
}

public remindersColumn = ['reminderDate', 'reminderSubject', 'reminderMessage', 'reminderSendMode', 'reminderActionIcons'];
public remindersData: ReminderTableItem[] = [];
public feedbackDueDate = new FormControl('');
public notificationData: any;
public DueDateEnabled: boolean;
public feedbackEnabled: boolean;
public accounts: any[] = [];
public initialFeedbackDueDate: Date | null = null;
public feedbackDueDateChanged = false;
private hasChanges = false;
private mappingSubscription?: Subscription;
private dueDateEnabledSub?: Subscription;
private feedbackEnabledSub?: Subscription;

// Callback for editing reminders in dialog mode
public editReminderCallback = (editedReminder: any, originalReminder: any) => {
  this.editReminderToAPI(editedReminder, originalReminder);
};

// Callback for deleting reminders in dialog mode
public deleteReminderCallback = (reminder: any) => {
  this.deleteReminderFromAPI(reminder);
};

public feedbackDueDateFilter = (d: Date | null): boolean => {
  const startDate = d || new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  return startDate > today;
}

public isReminderEditable(reminder: any): boolean {
  if (!reminder.reminderDate) return true;
  const reminderDate = new Date(reminder.reminderDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  reminderDate.setHours(0, 0, 0, 0);
  return reminderDate > today;
}

public isReminderRemovable(reminder: any): boolean {
  return this.isReminderEditable(reminder);
}

public validateReminderDate(reminderDate: Date, feedbackDueDate: Date): boolean {
  if (!reminderDate || !feedbackDueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  reminderDate.setHours(0, 0, 0, 0);
  feedbackDueDate.setHours(0, 0, 0, 0);
  return reminderDate > today && reminderDate < feedbackDueDate;
}

public areAllAccountsFeedbackOverdue(): boolean {
  if (!this.accounts || this.accounts.length === 0) {
    return false;
  }
  return this.accounts.every(account => 
    account.AccountStatus?.toLowerCase() === 'feedback overdue'
  );
}

public isFeedbackDueDateValid(): boolean {
  const feedbackDate = this.feedbackDueDate.value;
  
  if (!feedbackDate || this.feedbackDueDate.invalid) {
    return false;
  }
  
  // If there are no reminders, the date is valid
  if (!this.remindersData || this.remindersData.length === 0) {
    return true;
  }
  
  // Check if feedback due date is greater than all reminder dates
  const feedbackTime = new Date(feedbackDate).getTime();
  
  for (const reminder of this.remindersData) {
    if (reminder.reminderDate) {
      const reminderDate = this.parseReminderDate(reminder.reminderDate);
      if (reminderDate) {
        const reminderTime = reminderDate.getTime();
        if (feedbackTime <= reminderTime) {
          return false;
        }
      }
    }
  }
  
  return true;
}

public canSaveFeedbackDueDate(): boolean {
  return this.feedbackDueDateChanged && 
          this.feedbackDueDate.value !== null && 
          !this.feedbackDueDate.invalid &&
          this.isFeedbackDueDateValid();
}

private formatDate(date: Date): string {
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

private toDisplayDate(value: any): string | null {
  const parsed = this.parseReminderDate(value);
  if (!parsed) {
    return value ?? null;
  }
  return this.formatDate(parsed);
}

private getNotificationId(): string | undefined {
  return this.dialogData?.notificationId || this.notificationData?.NotificationId;
}

private getFeedbackDueDateIso(): string | null {
  const rawValue = this.feedbackDueDate.value;
  const dateValue = rawValue instanceof Date ? rawValue : new Date(rawValue);
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return null;
  }
  return dateValue.toISOString();
}

private applyApiReminders(reminders: ReminderDto[]): void {
  // Create a new array reference to trigger Angular change detection
  this.remindersData = [...this.normalizeReminderList(reminders)];
  if (!this.isInDialog()) {
    this.addProduct(this.feedbackDueDate.value, this.remindersData);
  }
  this.changeDetectorRef.markForCheck();
}


public setDateFilter(value: number): void {
  const nextDueDate = new Date();
  nextDueDate.setDate(nextDueDate.getDate() + value);
  this.feedbackDueDate.setValue(nextDueDate);
  if (this.isInDialog()) {
    this.hasChanges = true;
  } else {
    this.createReminderDialogService.setFeedbackDueDate(this.feedbackDueDate.value);
  }
}

public ngOnInit(): void {
  if (!this.isInDialog()) {
    this.createReminderDialogService.setRemindersDate([]);
  }
  
  // Handle dialog data initialization first
  if (this.dialogData) {
    // Use notificationData object directly
    if (this.dialogData.notificationData) {
      this.notificationData = this.dialogData.notificationData;
    }

    // Set feedback due date if provided
    const feedbackDueDate = this.dialogData.notificationData?.FeedbackDueDate || this.dialogData.notificationData?.DeadlineFeedback;
    if (feedbackDueDate) {
      this.feedbackDueDate.setValue(new Date(feedbackDueDate));
      this.initialFeedbackDueDate = new Date(feedbackDueDate);
    }

    // Set due date enabled toggle
    if (this.dialogData.notificationData?.DueDateEnabled !== undefined) {
      this.DueDateEnabled = this.dialogData.notificationData.DueDateEnabled;
      if (!this.isInDialog()) {
        this._QnSbnDataService.DueDateEnabled$.next(this.DueDateEnabled);
      }
    }

    // Set feedback enabled
    if (this.dialogData.feedbackEnabled !== undefined) {
      this.feedbackEnabled = this.dialogData.feedbackEnabled;
      if (!this.isInDialog()) {
        this._QnSbnDataService.FeedbackEnabled$.next(this.feedbackEnabled);
      }
    }

    // Set accounts data if provided
    if (this.dialogData.accounts) {
      this.accounts = this.dialogData.accounts;
    }

    // Use NotificationReminders from API response directly
    const apiReminders = this.dialogData?.notificationData?.NotificationReminders || [];
    this.remindersData = [...this.normalizeReminderList(apiReminders)];
    this.changeDetectorRef.markForCheck();
  }

  // Subscribe to data service updates (for non-dialog usage)
  if (!this.isInDialog()) {
    this.mappingSubscription = this._QnSbnDataService.mappingData$.subscribe(observer => {
      if (!this._QnSbnDataService.isEmpty(observer['feedbackDueDate'])) {
        this.feedbackDueDate.setValue(new Date(observer['feedbackDueDate']['feedbackDueDate']));
      }
      if (!this._QnSbnDataService.isEmpty(observer['notificationReminders'])) {
        // In non-dialog mode, data is already in table format (not DTO format)
        this.remindersData = observer['notificationReminders']['remindersData'];
        this.changeDetectorRef.detectChanges();
      }
      if (observer['localNotificationId']) {
        this.notificationData = observer['localNotificationId'];
      }
    });
  }
}

ngOnDestroy(): void {
  if (!this.isInDialog()) {
    this.addProduct(this.feedbackDueDate.value, this.remindersData);
  }
  this.mappingSubscription?.unsubscribe();
  this.dueDateEnabledSub?.unsubscribe();
  this.feedbackEnabledSub?.unsubscribe();
}

public getFeedbackDueDate(fdd: Date): void {
  if (this.isInDialog()) {
    this.hasChanges = true;
    this.checkFeedbackDueDateChanged();
  }
  if (!this.isInDialog()) {
    this._QnSbnDataService.getMappedQnsbnData({
      feedbackDueDate: this.feedbackDueDate.value
    }, 'FeedbackDueDate');
  }
}

public onFeedbackDueDateChange(event: any): void {
  if (this.isInDialog()) {
    this.hasChanges = true;
    // Use setTimeout to ensure the form control has been updated
    setTimeout(() => {
      this.checkFeedbackDueDateChanged();
      this.changeDetectorRef.detectChanges();
    }, 0);
  }
  if (!this.isInDialog()) {
    this._QnSbnDataService.getMappedQnsbnData({
      feedbackDueDate: this.feedbackDueDate.value
    }, 'FeedbackDueDate');
  }
}

public addProduct(newDate, reminderData) {
  if (!this.isInDialog()) {
    this._QnSbnDataService.getMappedQnsbnData({
      feedbackDueDate: newDate
    }, 'FeedbackDueDate');
    this._QnSbnDataService.getMappedQnsbnData({
      remindersData: reminderData
    }, 'NotificationReminders');
  }
}

public openReminderTemplateDialog(): void {
  if (this.feedbackDueDate.value && !this.isInDialog()) {
    this.createReminderDialogService.setFeedbackDueDate(this.feedbackDueDate.value);
  }

  const notificationType = this.notificationData?.Type;
  const notificationLanguage = this.notificationData?.LocalNotificationLanguage;

  if (!notificationType || !notificationLanguage) {
    // No notification type/language available, show blank template dialog
    this.displayRemainderTemplateDialog([]);
    return;
  }

  this.qnsbnservice.getReminderTemplate(notificationType, notificationLanguage).subscribe(
    (data: any) => {
      if (data.errorMessage) {
        this.displayRemainderTemplateDialog([]);
      } else {
        this.displayRemainderTemplateDialog(data.MessageTemplates || []);
      }
    },
    (error: HttpErrorResponse) => {
      // On error, show blank template dialog
      this.displayRemainderTemplateDialog([]);
    }
  );
}

public displayRemainderTemplateDialog(tempData) {
  const tableColumnData = ['dragHandle', 'selectText', 'messageTemplate', 'iconSets'];
  const templateDataList = tempData;
  const dialogRef = this.dialog.open(ReminderMessageTemplateDialogComponent, {
    data: {
      tableData: templateDataList,
      columnData: tableColumnData,
      feedbackDueDate: this.feedbackDueDate.value,
      isDialogMode: this.isInDialog(),
      notificationData: this.notificationData,
      remindersData: this.remindersData
    },
    disableClose: true,
  });

  dialogRef.afterClosed().subscribe(result => {
    console.log('ReminderMessageTemplateDialog closed with result:', result);
    console.log('isInDialog():', this.isInDialog());
    
    if (!this.isInDialog()) {
      console.log('Not in dialog mode, returning');
      return;
    }

    const created = Array.isArray(result?.createdReminders) ? result.createdReminders : [];
    const shouldCreateTemplate = result?.shouldCreateTemplate || false;
    console.log('Created reminders:', created);
    console.log('shouldCreateTemplate:', shouldCreateTemplate);
    
    created.forEach(reminder => {
      console.log('Calling saveReminderToAPI for reminder:', reminder);
      this.saveReminderToAPI(reminder, shouldCreateTemplate);
    });
  });
}

public openCreateReminderDialog(): void {
  if (this.feedbackDueDate.value && !this.isInDialog()) {
    this.createReminderDialogService.setFeedbackDueDate(this.feedbackDueDate.value);
  }

  const dialogRef = this.dialog.open(CreateReminderComponent, {
    data: {
      feedbackDueDate: this.feedbackDueDate.value,
      remindersData: this.remindersData,
      isEdit: false,
      isDialogMode: this.isInDialog(),
      isBlank: true,
      notificationData: this.notificationData
    },
    disableClose: true,
    autoFocus: false,
    panelClass: 'reminder-modalbox'
  });

  dialogRef.afterClosed().subscribe(result => {
    if (result && result.data) {
      if (this.isInDialog()) {
        const shouldCreateTemplate = result.shouldCreateTemplate || false;
        this.saveReminderToAPI(result.data[0], shouldCreateTemplate);
      } else {
        // Non-dialog mode: update local state
        const formattedReminders = result.data.map(reminder => {
          const parsedDate = this.parseReminderDate(reminder.reminderDate);
          return {
            ...reminder,
            reminderDate: parsedDate ? this.formatDate(parsedDate) : reminder.reminderDate
          };
        });
        this.remindersData = [...this.remindersData, ...formattedReminders];
        this.addProduct(this.feedbackDueDate.value, this.remindersData);
        this.changeDetectorRef.markForCheck();
      }
    }
  });
}

private saveReminderToAPI(newReminder: any, shouldCreateTemplate: boolean = false): void {
  if (!this.isInDialog()) {
    return;
  }

  if (!this.feedbackDueDate.value) {
    this.oneSnackBar.open('Please set a feedback due date before adding reminders', OneSnackBarType.WARNING, '', { duration: 5000 });
    return;
  }

  const notificationId = this.getNotificationId();
  const feedbackDueDateIso = this.getFeedbackDueDateIso();

  if (!notificationId || !feedbackDueDateIso) {
    this.oneSnackBar.open('Reminder could not be created. Please set a valid feedback due date.', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  // Convert reminder date to ISO format
  const parsedReminderDate = this.parseReminderDate(newReminder.reminderDate);
  const reminderDateIso = parsedReminderDate ? parsedReminderDate.toISOString() : newReminder.reminderDate;

  const reminderPayload = {
    NotificationId: notificationId,
    FeedbackDueDate: feedbackDueDateIso,
    DueDateEnabled: this.DueDateEnabled,
    NotficationReminders: [{
      ReminderDueDate: reminderDateIso,
      ReminderSubject: newReminder.reminderSubject || '',
      ReminderMessage: newReminder.reminderMessage || '',
      ReminderAddTemplate: newReminder.reminderAddTemplate || '',
      ReminderSendAutomatically: newReminder.reminderSendAutomatically !== undefined ? newReminder.reminderSendAutomatically : true
    }]
  };

  const successMessage = shouldCreateTemplate ? 'Reminder successfully added to notification and templates' : 'Reminder created successfully';
  
  // Call API to create reminder
  this.qnsbnservice.createReminders(reminderPayload).subscribe(
    (response: any) => {
      // Extract reminders from response - handle both direct and nested body structures
      const refreshedReminders = response?.body?.NotificationReminders || response?.NotificationReminders || [];
      this.applyApiReminders(refreshedReminders);
      this.hasChanges = true;
      this.changeDetectorRef.detectChanges();
      this.oneSnackBar?.open(successMessage, OneSnackBarType.SUCCESS, '', { duration: 5000 });
    },
    (error: HttpErrorResponse) => {
      this.qnsbnservice.httpErrorHandler(error, 'Failed to create reminder.');
    }
  );
}

private editReminderToAPI(editedReminder: any, originalReminder: any): void {
  if (!this.isInDialog()) {
    return;
  }

  if (!this.feedbackDueDate.value) {
    this.oneSnackBar.open('Please set a feedback due date before editing reminders', OneSnackBarType.WARNING, '', { duration: 5000 });
    return;
  }

  if (!originalReminder?.reminderId) {
    this.oneSnackBar.open('Cannot edit reminder: Missing reminder ID', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  const notificationId = this.getNotificationId();
  const feedbackDueDateIso = this.getFeedbackDueDateIso();

  if (!notificationId || !feedbackDueDateIso) {
    this.oneSnackBar.open('Reminder could not be updated. Please set a valid feedback due date.', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  // Convert reminder date to ISO format
  const parsedReminderDate = this.parseReminderDate(editedReminder.reminderDate);
  const reminderDateIso = parsedReminderDate ? parsedReminderDate.toISOString() : editedReminder.reminderDate;

  const editReminderPayload = {
    NotificationId: notificationId,
    FeedbackDueDate: feedbackDueDateIso,
    DueDateEnabled: this.DueDateEnabled,
    EditReminder: [{
      ReminderId: originalReminder.reminderId,
      ReminderDueDate: reminderDateIso,
      ReminderSubject: editedReminder.reminderSubject || '',
      ReminderMessage: editedReminder.reminderMessage || '',
      ReminderAddTemplate: editedReminder.reminderAddTemplate || '',
      ReminderSendAutomatically: editedReminder.reminderSendAutomatically !== undefined ? editedReminder.reminderSendAutomatically : true
    }]
  };

  // Call API to edit reminder
  this.qnsbnservice.createReminders(editReminderPayload).subscribe(
    (response: any) => {
      // Extract reminders from response - handle both direct and nested body structures
      const refreshedReminders = response?.body?.NotificationReminders || response?.NotificationReminders || [];
      this.applyApiReminders(refreshedReminders);
      this.hasChanges = true;
      this.changeDetectorRef.detectChanges();
      this.oneSnackBar?.open('Reminder updated successfully', OneSnackBarType.SUCCESS, '', { duration: 5000 });
    },
    (error: HttpErrorResponse) => {
      this.qnsbnservice.httpErrorHandler(error, 'Failed to update reminder.');
    }
  );
}

private deleteReminderFromAPI(reminder: any): void {
  if (!this.isInDialog()) {
    return;
  }

  if (!this.feedbackDueDate.value) {
    this.oneSnackBar.open('Please set a feedback due date before deleting reminders', OneSnackBarType.WARNING, '', { duration: 5000 });
    return;
  }

  const targetReminderId = reminder?.reminderId ?? reminder?.ReminderId;

  if (!targetReminderId) {
    this.oneSnackBar.open('Failed to delete reminder. Please close this popup and try again later.', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  const dueDateValue = this.feedbackDueDate.value instanceof Date
    ? this.feedbackDueDate.value
    : new Date(this.feedbackDueDate.value);

  if (!(dueDateValue instanceof Date) || Number.isNaN(dueDateValue.getTime())) {
    this.oneSnackBar.open('Invalid feedback due date. Please select the date again.', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  const notificationId = this.getNotificationId();
  if (!notificationId) {
    this.oneSnackBar.open('Failed to delete reminder. Please close this popup and try again later.', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  const deleteReminderPayload = {
    NotificationId: notificationId,
    FeedbackDueDate: dueDateValue.toISOString(),
    DueDateEnabled: this.DueDateEnabled,
    DeleteReminder: [{
      ReminderId: targetReminderId
    }]
  };

  // Call API to delete reminder
  this.qnsbnservice.createReminders(deleteReminderPayload).subscribe(
    (response: any) => {
      // Extract reminders from response - handle both direct and nested body structures
      const refreshedReminders = response?.body?.NotificationReminders || response?.NotificationReminders || [];
      this.applyApiReminders(refreshedReminders);
      this.hasChanges = true;
      this.changeDetectorRef.detectChanges();
      this.oneSnackBar?.open('Reminder deleted successfully', OneSnackBarType.SUCCESS, '', { duration: 5000 });
    },
    (error: HttpErrorResponse) => {
      this.qnsbnservice.httpErrorHandler(error, 'Failed to delete reminder.');
    }
  );
}

public updateEnabled(toggle: boolean): void {
  if (this.isInDialog() && this.DueDateEnabled !== toggle) {
    this.hasChanges = true;
  }
  if (!this.isInDialog()) {
    this._QnSbnDataService.DueDateEnabled$.next(toggle);
  }
}

public saveDueDateDisabled(): void {
  if (!this.isInDialog()) {
    return;
  }

  const notificationId = this.getNotificationId();
  
  if (!notificationId) {
    this.oneSnackBar.open('Failed to save. Please close this popup and try again later.', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  // First, delete all existing reminders if there are any
  if (this.remindersData && this.remindersData.length > 0) {

    const activeReminders = this.remindersData.filter(
      reminder => this.isReminderEditable(reminder) && reminder.reminderId
    );
    
    if (activeReminders.length > 0) {
      const deleteReminders = activeReminders.map(reminder => ({
        ReminderId: reminder.reminderId
      }));
    
      const deletePayload = {
        NotificationId: notificationId,
        FeedbackDueDate: '',
        DueDateEnabled: false,
        DeleteReminder: deleteReminders
      };
    
      // Call delete API first to remove all reminders
      this.qnsbnservice.createReminders(deletePayload).subscribe(
        (deleteResponse: any) => {
          // After successful deletion, disable the due date
          this.disableDueDateAfterDelete(notificationId);
        },
        (error: HttpErrorResponse) => {
          this.qnsbnservice.httpErrorHandler(error, 'Failed to delete reminders.');
        }
      );
    }

  } 
  else {
    // No reminders to delete, just disable the due date
    this.disableDueDateAfterDelete(notificationId);
  }
}

private disableDueDateAfterDelete(notificationId: string): void {
  const payload = {
    NotificationId: notificationId,
    FeedbackDueDate: '',
    DueDateEnabled: false,
    NotficationReminders: []
  };

  this.qnsbnservice.createReminders(payload).subscribe(
    (response: any) => {
      this.remindersData = [];
      this.feedbackDueDate.setValue(null);
      this.initialFeedbackDueDate = null;
      this.feedbackDueDateChanged = false;
      this.hasChanges = true;
      this.changeDetectorRef.detectChanges();
      this.oneSnackBar?.open('Due date disabled successfully', OneSnackBarType.SUCCESS, '', { duration: 5000 });
    },
    (error: HttpErrorResponse) => {
      this.qnsbnservice.httpErrorHandler(error, 'Failed to update reminders.');
    }
  );
}

private checkFeedbackDueDateChanged(): void {
  const currentDate = this.feedbackDueDate.value;
  
  if (!this.initialFeedbackDueDate && !currentDate) {
    this.feedbackDueDateChanged = false;
    return;
  }
  
  if (!this.initialFeedbackDueDate && currentDate) {
    this.feedbackDueDateChanged = true;
    return;
  }
  
  if (this.initialFeedbackDueDate && !currentDate) {
    this.feedbackDueDateChanged = true;
    return;
  }
  
  const initialTime = this.initialFeedbackDueDate.getTime();
  const currentTime = currentDate.getTime();
  this.feedbackDueDateChanged = initialTime !== currentTime;
}

public saveFeedbackDueDate(): void {
  if (!this.isInDialog()) {
    return;
  }

  const notificationId = this.getNotificationId();
  
  if (!notificationId) {
    this.oneSnackBar.open('Failed to save. Please close this popup and try again later.', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  if (!this.feedbackDueDate.value || this.feedbackDueDate.invalid) {
    this.oneSnackBar.open('Please select a valid feedback due date.', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  if (!this.isFeedbackDueDateValid()) {
    this.oneSnackBar.open('Feedback due date must be greater than all reminder dates.', OneSnackBarType.ERROR, '', { duration: 5000 });
    return;
  }

  // Get the current feedback due date from the form control - FORCE RE-READ
  const currentFeedbackDate = this.feedbackDueDate.value;
  
  // Create a new Date object and set time to noon to avoid timezone issues
  let feedbackDueDateIso: string | null = null;
  if (currentFeedbackDate) {
    const dateToSend = new Date(currentFeedbackDate);
    // Set time to 18:30 (which will be 00:00 next day in UTC for +0530 timezone)
    dateToSend.setHours(18, 30, 0, 0);
    feedbackDueDateIso = dateToSend.toISOString();
  }
  
  const payload = {
    NotificationId: notificationId,
    FeedbackDueDate: feedbackDueDateIso,
    DueDateEnabled: this.DueDateEnabled,
    NotficationReminders: []
  };

  this.qnsbnservice.createReminders(payload).subscribe(
    (response: any) => {
      // Extract reminders from response - handle both direct and nested body structures
      const refreshedReminders = response?.body?.NotificationReminders || response?.NotificationReminders || [];
      this.applyApiReminders(refreshedReminders);
      this.hasChanges = true;
      this.initialFeedbackDueDate = this.feedbackDueDate.value ? new Date(this.feedbackDueDate.value) : null;
      this.feedbackDueDateChanged = false;
      this.changeDetectorRef.detectChanges();
      this.oneSnackBar?.open('Feedback due date saved successfully', OneSnackBarType.SUCCESS, '', { duration: 5000 });
    },
    (error: HttpErrorResponse) => {
      this.qnsbnservice.httpErrorHandler(error, 'Failed to save feedback due date.');
    }
  );
}

public onDialogCancel(): void {
  if (this.dialogRef) {
    this.dialogRef.close({
      saved: this.hasChanges,
      reminders: this.remindersData.map(reminder => this.convertToApiReminder(reminder)),
      dueDateEnabled: this.DueDateEnabled,
      feedbackDueDate: this.feedbackDueDate.value ? this.feedbackDueDate.value.toISOString() : null
    });
  }
}

public isInDialog(): boolean {
  return !!this.dialogRef;
}

private normalizeReminderList(reminders: ReminderDto[]): ReminderTableItem[] {
  return (reminders || []).map(reminder => this.normalizeReminder(reminder));
}

private normalizeReminder(reminder: ReminderDto): ReminderTableItem {
  const sendAutomatically = reminder.ReminderSendAutomatically ?? true;
  
  return {
    reminderId: reminder.ReminderId,
    reminderDate: this.toDisplayDate(reminder.ReminderDueDate),
    reminderSubject: reminder.ReminderSubject ?? '',
    reminderMessage: reminder.ReminderMessage ?? '',
    reminderSendMode: sendAutomatically ? '0' : '1',
    reminderSendStatus: reminder.ReminderSendStatus ?? 'Not Sent',
    reminderAddTemplate: reminder.ReminderAddTemplate ?? '',
    reminderSendAutomatically: sendAutomatically,
    reminderActionIcons: true
  };
}

private parseReminderDate(value: any): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed);
    }
    const match = /^([0-9]{2})-([A-Za-z]{3})-([0-9]{4})$/.exec(value.trim());
    if (match) {
      const [, day, month, year] = match;
      return new Date(`${month} ${day}, ${year}`);
    }
  }
  return null;
}
  
private convertToApiReminder(reminder: any): any {
  const parsedDate = this.parseReminderDate(reminder.reminderDate);
  const formattedDate = parsedDate ? this.formatDate(parsedDate) : reminder.reminderDate || '';
  
  return {
    ReminderId: reminder.reminderId,
    ReminderDueDate: formattedDate,
    ReminderSubject: reminder.reminderSubject || '',
    ReminderMessage: reminder.reminderMessage || '',
    ReminderAddTemplate: reminder.reminderAddTemplate || '',
    ReminderSendStatus: reminder.reminderSendStatus || 'Not Sent',
    ReminderSendAutomatically: reminder.reminderSendAutomatically ?? true
  };
}
}